import React, { useEffect, useRef, useState } from "react";
import {
  TextField, Button, IconButton, InputAdornment, CircularProgress, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert,
  Box, Tooltip, Typography, InputBase
} from "@mui/material";
import { Send, AttachFile, Mic, Close, ErrorOutline } from "@mui/icons-material";
import {
  ChatMain, MessagesWrapper, InputWrapper, MessageBubble, Overlay, StyledInputWrapper,
  MessageContentWrapper,
} from "./styled";
import BarChartIcon from '@mui/icons-material/BarChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ChartModal from './ChartModal';
import { SendButton } from './styled';
import { ChartSuggestion, fetchChartSuggestion } from "../hooks/useChartSuggestion";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { DatabaseInfo } from "../types/chat";
import dayjs from "dayjs";

interface Message {
  id: string;
  text?: string;
  type: 'user' | 'bot' | 'error';
  tableData?: { headers: string[], rows: Record<string, any>[] };
  isTableExpanded?: boolean;
  explanation?: string;
  timestamp?: string;
}

function handleDownloadPdf(msg: Message) {
  const doc = new jsPDF();
  const headers = [msg.tableData?.headers ?? []];
  const rows = msg.tableData?.rows?.map(row =>
    (msg.tableData?.headers ?? []).map(h => String(row[h] ?? ""))
  );
  doc.text(msg.text || 'Risultati Tabella', 14, 14);
  autoTable(doc, {
    head: headers,
    body: rows,
    startY: 22,
    styles: { fontSize: 10 }
  });
  doc.save('tabella.pdf');
}

const DBS: DatabaseInfo[] = [
  { id: "wt_mouldplast", name: "Wt_Mouldplast" },
  { id: "self", name: "Self" },
  { id: "wpdemo1", name: "Workplan" },
];
const defaultDB = DBS.find(d => d.id === "self")!;

interface ChatMainViewProps {
  input: string;
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setInput: (val: string) => void;
  messages: Message[];
  isLoading: boolean;
  sendMessage: () => void;
  showPopup: boolean;
  setShowPopup: (val: boolean) => void;
  handleToggleTableExpand: (id: string) => void;
  chatId?: string;
}

const ChatMainView: React.FC<ChatMainViewProps> = ({
  input, setInput, messages, isLoading, sendMessage, showPopup, setShowPopup,
  handleToggleTableExpand, isSidebarCollapsed, toggleSidebar, chatId
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [chartMessage, setChartMessage] = useState<Message | null>(null);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [chartLoadingId, setChartLoadingId] = useState<string | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const [chartData, setChartData] = useState<{
    headers: string[],
    rows: Record<string, any>[],
    suggestion: ChartSuggestion,
  } | null>(null);

  const handleShowChart = async (msg: Message) => {
    if (!msg.tableData) return;
    try {
      setChartLoadingId(msg.id);
      const suggestion = await fetchChartSuggestion(msg.tableData.headers, msg.tableData.rows);
      const normalizedSuggestion: ChartSuggestion = {
        ...suggestion,
        y_axis: Array.isArray(suggestion.y_axis)
          ? suggestion.y_axis
          : [suggestion.y_axis],
      };
      setChartData({
        headers: msg.tableData.headers,
        rows: msg.tableData.rows,
        suggestion: normalizedSuggestion,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Errore sconosciuto";
      alert("Errore nella generazione del grafico: " + errorMessage);
    } finally {
      setChartLoadingId(null);
    }
  };

  const inputRefEl = useRef<HTMLTextAreaElement | null>(null);

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    if (inputRefEl.current) {
      inputRefEl.current.style.height = "80px";
    }
    await sendMessage();
  };

  return (
    <ChatMain isSidebarCollapsed={isSidebarCollapsed}>
      <MessagesWrapper>
        <Box sx={{ maxWidth: 960, mx: 'auto', width: '100%', px: 3 }}>
          {messages
            .filter((msg) =>
              msg && (msg.text || msg.explanation || (msg.tableData?.headers?.length && msg.tableData?.rows?.length))
            )
            .map((msg) => {
              const shouldRenderTable = msg.type === 'bot' && msg.tableData?.headers?.length && msg.tableData.rows?.length;
              const shouldRenderExplanation = !!msg.explanation;
              const collapsedRowCount = 10;

              return (
                <MessageBubble key={msg.id} isUser={msg.type === 'user'}>
                  <MessageContentWrapper isUser={msg.type === 'user'}>
                    {msg.explanation && msg.explanation !== msg.text && (
                      <div style={{ whiteSpace: 'pre-wrap', marginBottom: '8px' }}>
                        {msg.explanation}
                      </div>
                    )}
                    {msg.text && (
                      <div>
                        {msg.text}
                      </div>
                    )}
                    {shouldRenderTable && (
                      <>
                        <Paper
                          elevation={0}
                          sx={{
                            maxWidth: 720,
                            mt: 2,
                            mb: 2,
                            borderRadius: "20px",
                            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
                            border: "1px solid #e0e4ec",
                            backgroundColor: "#ffffff",
                            overflow: "hidden",
                            transition: "box-shadow 0.25s ease",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              px: 1.5,
                              py: 1,
                              backgroundColor: "#f6f8fc",
                              borderBottom: "1px solid #e3e7ef",
                            }}
                          >
                            <Tooltip title="Mostra grafico">
                              <IconButton
                                size="small"
                                aria-label="Mostra il grafico"
                                onClick={() => handleShowChart(msg)}
                                sx={{
                                  color: '#1976d2',
                                  opacity: 0.5,
                                  mr: 0.5,
                                  '&:hover': { color: '#1565c0', opacity: 1, background: '#e3f2fd' },
                                  transition: 'opacity 0.18s'
                                }}
                              >
                                {chartLoadingId === msg.id ? (
                                  <CircularProgress size={18} sx={{ color: '#1976d2' }} />
                                ) : (
                                  <BarChartIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Scarica come PDF">
                              <IconButton
                                size="small"
                                aria-label="Scarica PDF"
                                onClick={() => handleDownloadPdf(msg)}
                                sx={{
                                  color: '#d32f2f',
                                  opacity: 0.5,
                                  '&:hover': { color: '#b71c1c', opacity: 1, background: '#fbe9e7' },
                                  transition: 'opacity 0.18s'
                                }}
                              >
                                <PictureAsPdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <TableContainer
                            sx={{
                              maxHeight: msg.isTableExpanded ? 'none' : '350px',
                              background: '#ffffff'
                            }}
                          >
                            <Table stickyHeader size="small">
                              <TableHead>
                                <TableRow sx={{ background: '#e9f3fb' }}>
                                  {msg.tableData!.headers.map(header => (
                                    <TableCell
                                      key={header}
                                      sx={{
                                        fontWeight: 700,
                                        background: '#e9f3fb',
                                        color: '#155fa0',
                                        borderBottom: '2px solid #dbeafe',
                                        fontSize: 15
                                      }}
                                    >
                                      {header}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {msg.tableData!.rows
                                  .slice(0, msg.isTableExpanded ? msg.tableData!.rows.length : collapsedRowCount)
                                  .map((row, i) => (
                                    <TableRow
                                      key={i}
                                      sx={{
                                        '&:nth-of-type(odd)': { backgroundColor: '#f6fafd' },
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        transition: 'background 0.2s'
                                      }}
                                    >
                                      {msg.tableData!.headers.map(h => (
                                        <TableCell
                                          key={h}
                                          sx={{
                                            borderBottom: '1px solid #f0f1f7',
                                            fontSize: 14,
                                            color: '#28344b'
                                          }}
                                        >
                                          {String(row[h] ?? '')}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {msg.tableData!.rows.length > collapsedRowCount && (
                            <Button
                              onClick={() => handleToggleTableExpand(msg.id)}
                              size="small"
                              variant="text"
                              sx={{
                                ml: 1,
                                color: '#1976d2',
                                fontWeight: 500,
                                '&:hover': {
                                  backgroundColor: '#e3f2fd',
                                },
                              }}
                            >
                              {msg.isTableExpanded
                                ? 'Mostra meno'
                                : `Mostra tutto (${msg.tableData!.rows.length})`}
                            </Button>
                          )}
                        </Paper>
                      </>
                    )}
                    {msg.type === 'bot' && msg.timestamp && (
                      <div style={{
                        marginTop: "10px",
                        fontSize: "0.75em",
                        opacity: 0.7,
                        textAlign: "right"
                      }}>
                        {dayjs(msg.timestamp).format("HH:mm DD/MM")}
                      </div>
                    )}
                    {msg.type === 'error' && <ErrorOutline color="error" sx={{ fontSize: '16px', ml: '5px' }} />}
                  </MessageContentWrapper>
                </MessageBubble>
              );
            })}
          <div ref={messagesEndRef} />
        </Box>
      </MessagesWrapper>

      {showPopup && (
        <>
          <Overlay onClick={() => setShowPopup(false)} />
          <Alert
            severity="info"
            sx={{
              position: "fixed",
              top: "10%",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1301
            }}
          >
            Working on microphone functionality...
          </Alert>
        </>
      )}

      <StyledInputWrapper>
        <Box sx={{ maxWidth: 960, mx: 'auto', width: '100%', px: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #d2d6e2',
              borderRadius: '24px',
              backgroundColor: '#f1f6ff',
              paddingY: 1,
              paddingX: 2,
              overflow: "hidden",
              maxWidth: "100%",
              "&:focus-within": {
                borderColor: "#1c90ff",
              },
            }}
          >
            <Box
              component="textarea"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                const el = e.target as HTMLTextAreaElement;
                el.style.height = "auto";
                el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                  setTimeout(() => inputRefEl.current?.focus(), 0);
                }
              }}
              ref={inputRefEl}
              rows={1}
              sx={{
                width: "100%",
                maxHeight: "200px",
                overflowY: "auto",
                border: "none",
                outline: "none",
                minHeight: "80px",
                resize: "none",
                height: "80px",
                fontSize: "1rem",
                paddingY: "12px",
                paddingX: "16px",
                backgroundColor: "#f1f6ff",
                borderRadius: "20px",
                lineHeight: 1.5,
                fontFamily: "inherit",
                transition: "height 0.2s ease",
              }}
            />
            <Tooltip title="Allega file">
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  width: 40,
                  height: 40,
                  mr: 1,
                  color: "#1c3477",
                  '&:hover': { backgroundColor: '#e3f2fd' },
                }}
              >
                <AttachFile fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Microfono (in sviluppo)">
              <IconButton
                onClick={() => setShowPopup(true)}
                sx={{
                  width: 40,
                  height: 40,
                  mr: 1,
                  color: "#1c3477",
                  '&:hover': { backgroundColor: '#e3f2fd' },
                }}
              >
                <Mic fontSize="small" />
              </IconButton>
            </Tooltip>
            <SendButton
              onClick={handleSendMessage}
              disabled={isLoading || !input.trim()}
              sx={{
                width: 44,
                height: 44,
                minWidth: 0,
                borderRadius: '50%',
                backgroundColor: "#1c3477",
                color: "#ffffff",
                marginLeft: "8px",
                "&:hover": {
                  backgroundColor: "#142654",
                  transform: "scale(1.05)",
                },
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                <Send fontSize="small" sx={{ color: "#ffffff" }} />
              )}
            </SendButton>
          </Box>
        </Box>
        <input type="file" ref={fileInputRef} style={{ display: "none" }} />
      </StyledInputWrapper>
      {chartData && chartData.suggestion && (
        <ChartModal
          open={true}
          onClose={() => setChartData(null)}
          headers={chartData.headers}
          rows={chartData.rows}
          x_axis={chartData.suggestion.x_axis}
          y_axis={chartData.suggestion.y_axis}
          chart_type={chartData.suggestion.chart_type}
        />
      )}
    </ChatMain>
  );
};

export default ChatMainView;
